import { loadScript } from '../utils/load-script.js';
import { makeUUID } from '../utils/refi-mfe-uuid.js';
import { trackAnalyticsEvent } from '../utils/track-analytics-event.js';
import '../utils/wait-for-dom.js';

class AdobeTargetService {
  constructor() {
    this.adobeScriptSrc = 'https://www.capitalone.com/assets/shell/adobe/at.js';
    this.queuedMboxCalls = [];
    this.session = JSON.parse(sessionStorage.getItem('auto-refi-session'));
    document.addEventListener('at-request-succeeded', this.addExperienceNote.bind(this));
    this.adobeTargetLoad = new Promise(resolve => {
      if ('adobe' in window) {
        resolve(true);
      } else {
        document.addEventListener('at-library-loaded', function onLoad() {
          resolve(true);
          document.removeEventListener('at-library-loaded', onLoad);
        });
      }
    });
    this.testsNotes = [];
    this.loadScript();
    this._noteUrl = 'api/Application/auth/saveNote';

    ['at-content-rendering-failed', 'at-request-failed', 'at-content-rendering-succeeded', 'at-content-rendering-no-offers'].forEach(eventType => {
      document.addEventListener(eventType, this.dispatchTestDone.bind(this));
    });
  }

  get noteUrl() {
    return this._noteUrl;
  }

  set noteUrl(url) {
    this._noteUrl = url;
  }

  dispatchTestDone(evt) {
    document.dispatchEvent(
      new CustomEvent('refi-at-done', {
        detail: {
          type: evt.detail.type,
          mbox: evt.detail.mbox,
          originalEvent: evt,
        },
      }),
    );
  }

  async addExperienceNote(ev) {
    if (ev.detail.mbox.indexOf('apply') === -1) return;
    const responseToken = ev.detail.responseTokens && ev.detail.responseTokens[0];
    if (responseToken && this.session && !this.testsNotes.some(test => test.activityName === responseToken['activity.name'] && test.experienceName === responseToken['experience.name'])) {
      const { eAppId } = this.session;
      const activityName = responseToken['activity.name'];
      const experienceName = responseToken['experience.name'];
      const pathName = window.location.pathname;
      const pageName = this.getPageName(pathName);
      const HTTP_HEADERS = {
        Accept: 'application/json;v=1',
        'Content-Type': 'application/json;v=1',
        eapp_id: eAppId,
        'Client-Correlation-Id': makeUUID(),
      };
      const options = {
        headers: HTTP_HEADERS,
        withCredentials: true,
        method: 'POST',
        body: JSON.stringify({
          eAppId,
          experienceName,
          noteType: 'EXPERIENCE_NAME_NOTE',
          pageName,
        }),
      };
      this.testsNotes.push({ activityName, experienceName });
      await fetch(this._noteUrl, options);
    }
  }

  async loadScript() {
    if (!document.querySelector(`[src="${this.adobeScriptSrc}"]`)) {
      const boundCallQueuedMboxes = this.callQueuedMboxes.bind(this);
      const script = await loadScript(this.adobeScriptSrc, 'targetScript', true);
      script.addEventListener('load', function onLoad() {
        boundCallQueuedMboxes();
        script.removeEventListener('load', onLoad);
      });
    }
  }

  callQueuedMboxes() {
    if (this.queuedMboxCalls.length > 0) {
      this.queuedMboxCalls.forEach(mboxCall => {
        this.getOffer(mboxCall)
          .then(this.applyOffer.bind(this))
          .catch(() => {});
      });
    }
  }

  waitForAdobeTargetToLoad() {
    return this.adobeTargetLoad;
  }

  getOffer(mboxOpts = {}) {
    return new Promise((resolve, reject) => {
      if ('adobe' in window) {
        if (!mboxOpts.mbox) {
          trackAnalyticsEvent('adobe-target-failed-no-mbox');
          reject(new Error('No mbox is defined'));
        }
        const atOpts = {
          mbox: mboxOpts.mbox,
          params: mboxOpts.params,
          timeout: mboxOpts.timeout,
          success: resp => {
            if (resp && resp.length > 0) {
              resolve({
                mbox: mboxOpts.mbox,
                offer: resp,
                selector: mboxOpts.selector,
              });
            } else {
              trackAnalyticsEvent(`adobe-target-failed-empty-target-${mboxOpts.mbox}`);
              reject(new Error('Empty target offer'));
              this.dispatchTestDone({
                detail: {
                  type: 'at-content-rendering-no-offers',
                  mbox: mboxOpts.mbox,
                },
              });
            }
          },
          error: (status, error) => {
            reject(error);
          },
        };
        window.adobe.target.getOffer(atOpts);
      } else {
        this.queuedMboxCalls.push(mboxOpts);
        trackAnalyticsEvent(`adobe-target-failed-not-initialized-${mboxOpts.mbox}`);
        reject(new Error('Adobe Target not initialized yet'));
      }
    });
  }

  async applyOffer(offer) {
    try {
      window.adobe.target.applyOffer(offer);
      return offer;
    } catch {
      this.dispatchTestDone({
        detail: {
          type: 'at-content-rendering-failed',
          mbox: offer.mbox,
        },
      });
      return false;
    }
  }

  getPageName(pathName) {
    switch (pathName) {
      case '/apply/offers':
        return 'Offers';
      case '/apply/processing-offer':
        return 'Hard Pull Processing';
      case '/apply/e-sign':
        return 'eSign Contract';
      case '/apply/lender-info':
        return 'Lender';
      default:
        return '';
    }
  }
}

const RefiAdobeTarget = new AdobeTargetService();

export { RefiAdobeTarget };
